import Konva from 'konva'
import RenElement from './ren-element.es6'
import RenElementAdding from './ren-element-adding.es6'
import RenMutator from './ren-mutator.es6'
import isEqual from 'lodash.isequal'
import store from './../../../store.es6'

const grid = { span: 10, extent: 10000 }
const backgroundElements = { opacity: 0.2 }
const zoomDebounceDelay = 100
export const minZoomLevel = 0.03
export const maxZoomLevel = 10
// possible performance quick fix for Retina displays
Konva.pixelRatio = 1

export default class RenStage extends Konva.Stage {
  constructor (attrs, renAttrs, eventHandlers) {
    super(attrs)
    // Properties
    this._levelId = undefined
    this._elements = []
    this._backgroundElements = []
    this._assetData = {}
    this._deviceTypes = {}
    this._selectionIds = []
    this._visualMode = undefined
    this._editMode = undefined
    this.sizeAdjustment = undefined
    this.gridSpan = grid.span
    this.gridExtent = grid.extent
    this.zoomLevel = 1
    this._scale = 1
    Object.assign(this, renAttrs)
    // Event handlers (must bind this from _eventsParent)
    this.handleElementsSelected = eventHandlers.handleElementsSelected.bind(this._eventsParent)
    this.handleUpdateManyElements = eventHandlers.handleUpdateManyElements.bind(this._eventsParent)
    this.handleDestroyManyElements = eventHandlers.handleDestroyManyElements.bind(this._eventsParent)
    this.handleZoomLevelChanged = eventHandlers.handleZoomLevelChanged.bind(this._eventsParent)

    // Local properties
    this.parentContainer = document.querySelector('#stage-parent')
    this._addingElementMode = false // True when addingElement by user
    this._fitStageTimeout = undefined // Function to be executed after the timeout

    // Fit to screen
    this.fitStageIntoParentContainer()

    // Init layers (order matters - z-index)
    const renLayers = []
    renLayers.push(new Konva.Layer({ name: 'bottom' }))
    renLayers.push(new Konva.Layer({ name: 'grid' }))
    renLayers.push(new Konva.Layer({ name: 'backgroundElements', opacity: backgroundElements.opacity }))
    renLayers.push(new Konva.Layer({ name: 'elements', pattern: 'area' }))
    renLayers.push(new Konva.Layer({ name: 'elements', pattern: 'vector' }))
    for (const typ of ['pme']) { // pattern == 'node'
      renLayers.push(new Konva.Layer({ name: 'elements', pattern: 'node', typ: typ }))
    }
    renLayers.push(new Konva.Layer({ name: 'elements', pattern: 'vector', typ: 'tc' }))
    renLayers.push(new Konva.Layer({ name: 'elements', pattern: 'node' }))
    renLayers.push(new Konva.Layer({ name: 'absoluteSizeElements' }))
    renLayers.push(new Konva.Layer({ name: 'mutators' }))
    renLayers.push(new Konva.Layer({ name: 'elementLabels' }))
    renLayers.push(new Konva.Layer({ name: 'top' }))

    // Add layers to the stage
    renLayers.forEach(layer => this.add(layer))

    // Init grid and bottom
    this._renderGrid()
    this._renderBottom()

    // Bind non-own events (must be unbound on destroy)
    this.evtHandleStageFit = this.fitStageIntoParentContainer.bind(this)
    window.addEventListener('resize', this.evtHandleStageFit, true)

    this.evtHandleKeyDown = this.onKeyDown.bind(this)
    window.addEventListener('keydown', this.evtHandleKeyDown, true)
    // Bind events
    // window.addEventListener('resize', this.fitStageIntoParentContainer.bind(this))
    // document.onkeydown = this.onKeyDown.bind(this)
    this.on('mouseup', function (event) {
      const stageAttrs = this.attrs
      this.currentVisual.positionX = stageAttrs.x / stageAttrs.scaleX
      this.currentVisual.positionY = stageAttrs.y / stageAttrs.scaleY
      store.dispatch('updateItem', { modelName: 'visuals', item: this.currentVisual })
    })
    this.on('contentClick', function (event) {
      this.endSelectionMode(event)
    })
  }

  destroy () {
    // Unbind all events bound on other components
    window.removeEventListener('resize', this.evtHandleStageFit, true)
    window.removeEventListener('keydown', this.evtHandleKeyDown, true)
    // Destroy
    super.destroy()
  }

  // GETTERS AND SETTERS

  findLayer (name) {
    return [...this.children].find(layer => layer.attrs.name === name)
  }

  getElementLayers () {
    return this.children.filter(layer => ['elements', 'absoluteSizeElements'].includes(layer.attrs.name))
  }

  set elements (value) {
    // Elements to be assigned only via this setter as it performes validation
    console.log('SETTING NEW ELEMENTS - running expensive update')
    this._elements = value.filter(element => element.position && element.position.points)
    // console.log(this._elements)
    this._updateAllElements()
    // this._updateAllMutators()
  }

  set backgroundElements (value) {
    this._backgroundElements = value.filter(element => element.position && element.position.points)
    this._updateBackgroundElements()
  }

  set assetData (value) {
    // Set
    this._assetData = value

    // QFIX: !!! Update all elements when assetData change!
    // this._updateAllElements()
  }

  updateElement (assetId, newVariables, deviceTypes = undefined) {
    // console.log("GETTING MESSAGE FROM STORE TO UPDATE ELEMENT")
    // find element somehow (by asset id)
    // update its variables
    // redraw it

    // getting all elements
    // const elementLayers = this.getElementLayers()
    // const elements = elementLayers.map(layer => {
    //   return layer.getChildren().map(renElement => renElement)
    // }).flat()

    // console.log("CURRENT ELEMENTS ON STAGE")
    // console.log(this.renElements)
    // console.log()

    this._updateElementShapes(assetId, newVariables, deviceTypes)
    this._redrawAllLayers()
    // chosenElement._updateAllShapes()
  }

  updateElementsFromFeed (assets) {
    for (const assetId of Object.keys(assets)) {
      const { variables } = assets[assetId]
      this._updateElementShapes(assetId, variables)
    }
    this._redrawAllLayers()
  }

  _updateElementShapes (assetId, newVariables, deviceTypes = undefined) {
    // try to find element on stage
    const renElement = this.renElements.find(element => element.assetId === assetId)

    // update element if found
    if (renElement) {
      // console.log("Trying to update found element")
      renElement._updateAllShapes(newVariables, deviceTypes)
    }
  }

  _redrawAllLayers () {
    const elementLayers = this.getElementLayers()
    elementLayers.forEach(layer => {
      layer.batchDraw()
    })
  }

  resetAllElements (variables = {}) {
    this.renElements.forEach(element => {
      element._updateAllShapes(variables)
    })

    // redraw all layers to show changes
    const elementLayers = this.getElementLayers()
    elementLayers.forEach(layer => {
      layer.batchDraw()
    })
  }

  get selectionIds () {
    return this._selectionIds
  }

  set selectionIds (value) {
    // Memorize previously selected elements
    const affectedRenElements = new Set(this.selectedRenElements)

    // Set
    this._selectionIds = value

    // Add the newly selected elements to the list of affected elements
    this.selectedRenElements.forEach(renElement => {
      affectedRenElements.add(renElement)
    })

    // Update all related elements (were and are selected)
    affectedRenElements.forEach(renElement => {
      renElement.selected = this._selectionIds.includes(renElement._element.id)
    })

    // Update the mutators
    // this._updateAllMutators()
  }

  get visualMode () {
    return this._visualMode
  }

  set visualMode (value) {
    this._visualMode = value
    this.renElements.forEach(renElement => {
      renElement.visualMode = value
    })
    this._renderGrid()
  }

  get addingElementMode () {
    return this._addingElementMode
  }

  set addingElementMode (value) {
    this._addingElementMode = value
    if (value) {
      // Cancel selection of any elements
      this.handleElementsSelected([], false)
    }
    // this.renElements.forEach(renElement => { renElement.reactToStageChanges() })
  }

  get selRectMode () {
    return !!this.getRenElement('selRect', 'mutators')
  }

  get renElementAdding () {
    return this.findLayer('top').findOne('#adding')
  }

  set editMode (value) {
    this._editMode = value

    this._renderGrid()
  }

  get renElements () {
    return this.getElementLayers().reduce((elements, layer) => {
      layer.children.forEach(element => {
        elements.push(element)
      })
      return elements
    }, [])
  }

  get selectedRenElements () {
    return this.getElementLayers().reduce((selectedElements, layer) => {
      layer.children.forEach(renElement => {
        if (this._selectionIds.includes(renElement._element.id)) {
          selectedElements.push(renElement)
        }
      })
      return selectedElements
    }, [])
  }

  get stagePosition () {
    const pointerPosition = this.getPointerPosition()
    const stageAttrs = this.attrs
    return {
      x: (pointerPosition.x - stageAttrs.x) / stageAttrs.scaleX,
      y: (pointerPosition.y - stageAttrs.y) / stageAttrs.scaleY
    }
  }

  set levelId (levelId) {
    this._levelId = levelId
  }

  getRenElement (renId, layerName) {
    if (layerName) {
      const layer = this.findLayer(layerName)
      if (layer) {
        return layer.findOne(`#${renId}`)
      }
    }
    return this.findOne(`#${renId}`)
  }

  // METHODS

  _getElementDiffData ({ id, pattern, position, asset, assetId, elementType }) {
    return {
      x: pattern === 'node' ? position.points[0] : 0,
      y: pattern === 'node' ? position.points[1] : 0,
      rotation: pattern === 'node' ? position.rotation : 0,
      assetId: asset && asset.id,
      assetData: this._assetData[assetId],
      selected: this._selectionIds.includes(id),
      labelRotation: (position.label && position.label.rotation) || 0,
      labelPosition: position.label && position.label.position,
      absoluteSizeScale: elementType.absoluteSize ? this._scale / this.sizeAdjustment : 1
    }
  }

  _createRenElement (element, mutatorsLayer, elementLabelsLayer) {
    const diffData = this._getElementDiffData(element)
    const renElement = new RenElement(
      {
        // Konva properties
        x: diffData.x,
        y: diffData.y,
        rotation: diffData.rotation
      },
      {
        // Ren properties
        _element: Object.assign({}, element),
        _assetData: diffData.assetData,
        _deviceTypes: this._deviceTypes,
        _selected: diffData.selected,
        _labelRotation: diffData.labelRotation,
        _labelPosition: diffData.labelPosition,
        _visualMode: this._visualMode,
        absoluteSizeScale: diffData.absoluteSizeScale,
        mutatorsLayer,
        elementLabelsLayer,
        renStage: this,
        _eventsParent: this
      },
      {
        // Ren events
        handleClick: this.onElementClick,
        handleDragmove: this.onElementDragmove,
        handleUpdate: this.onElementUpdate
      }
    )
    renElement.setId(element.id)
    return renElement
  }

  _updateAllElements () {
    const start = new Date()

    const elementLayers = this.getElementLayers()
    const absoluteSizeElementsLayer = this.findLayer('absoluteSizeElements')
    const mutatorsLayer = this.findLayer('mutators')
    const elementLabelsLayer = this.findLayer('elementLabels')

    const startRecreate = new Date()
    const recreate = this._elements
      .map(updatedElement => {
        const layer = updatedElement.elementType.absoluteSize ? absoluteSizeElementsLayer
          : elementLayers.find(layer =>
            layer.attrs.pattern === updatedElement.pattern &&
            (layer.attrs.typ === updatedElement.deviceTyp || !layer.attrs.typ))
        const diffData = this._getElementDiffData(updatedElement)
        const renElement = layer.findOne('#' + updatedElement.id)
        const created = !renElement
        const changed = true || (!created && !isEqual(diffData, renElement.diffData))
        // console.log('Recreate', {created, changed, diffFr: renElement && renElement.diffData, diffTo: diffData})
        return { updatedElement, layer, renElement, created, changed }
      })
      .filter(({ created, changed }) => created || changed)
    const endRecreate = new Date()

    const startDestroy = new Date()
    const updatedIds = this._elements.map(({ id }) => id)
    let destroy = []
    elementLayers.forEach(layer => {
      const layerDestroy = layer
        .getChildren(renElement => !updatedIds.includes(renElement.getId()))
        .map(renElement => ({ layer, renElement }))
      destroy = destroy.concat([...layerDestroy])
    })
    const endDestroy = new Date()

    const startRecreated = new Date()
    recreate.forEach(({ updatedElement, layer, renElement }) => {
      renElement && renElement.destroy()

      const newRenElement = this._createRenElement(updatedElement, mutatorsLayer, elementLabelsLayer)
      layer.add(newRenElement)
    })
    const endRecreated = new Date()

    const startDestroyed = new Date()
    destroy.forEach(({ renElement }) => {
      renElement.destroy()
    })
    const endDestroyed = new Date()

    const startDraw = new Date()
    const changedLayers = [...new Set([...recreate, ...destroy].map(({ layer }) => layer))]
    changedLayers.forEach(layer => layer.draw())
    const endDraw = new Date()

    const end = new Date()
    console.log(`[ren-stage][_updateAllElements] #${recreate.length + destroy.length} @ ${end - start},` +
      ` recreate @ ${endRecreate - startRecreate}, destroy @ ${endDestroy - startDestroy},` +
      ` recreated #${recreate.length} @ ${endRecreated - startRecreated},` +
      ` destroyed #${destroy.length} @ ${endDestroyed - startDestroyed},` +
      ` draw #${changedLayers.length} @ ${endDraw - startDraw}`)
  }

  _updateBackgroundElements () {
    console.log('[ren-stage][_updateBackgroundElements]')

    const layer = this.findLayer('backgroundElements')
    layer.destroyChildren()

    const emptyHandler = () => undefined

    this._backgroundElements.forEach(element => {
      const renElement = new RenElement(
        {
          // Konva properties
          x: element.pattern === 'node' ? element.position.points[0] : 0,
          y: element.pattern === 'node' ? element.position.points[1] : 0,
          rotation: element.pattern === 'node' ? element.position.rotation : 0
        },
        {
          // Ren properties
          _element: Object.assign({}, element),
          _assetData: this._assetData[element.assetId],
          _deviceTypes: this._deviceTypes,
          _selected: false,
          _visualMode: 'watch',
          renStage: this,
          _eventsParent: this
        },
        {
          // Ren events
          handleClick: emptyHandler,
          handleDragmove: emptyHandler,
          handleUpdate: emptyHandler
        }
      )

      layer.add(renElement)
    })

    layer.draw()
  }

  _renderGrid () {
    // Destroy existing grid
    const gridLayer = this.findLayer('grid')
    gridLayer.hitGraphEnabled(false)
    gridLayer.destroyChildren()

    // Create new grid
    const renGrid = new RenMutator.Grid({
      span: this.gridSpan,
      extent: this.gridExtent,
      detailed: this._editMode,
      zoomLevel: this.zoomLevel
    })
    gridLayer.add(renGrid)
    gridLayer.draw()
  }

  _renderBottom () {
    // Bottom layer for catching canvas click events
    // Size same as the grid extent

    // (does not get re-rendered so no destroy is needed)
    // Create new
    const bottomLayer = this.findLayer('bottom')
    const renBottom = new RenMutator.Bottom(
      {
        id: 'bottom'
      },
      {
        // Ren properties
        gridExtent: this.gridExtent,
        renStage: this,
        _eventsParent: this
      },
      {
        // Ren events
        handleClick: this.startSelectionMode
      }
    )
    bottomLayer.add(renBottom)
    bottomLayer.draw()
  }

  _renderSelRect () {
    // (does not get re-rendered so no destroy is needed)
    // Create new
    const layer = this.findLayer('mutators')
    const renSelRect = new RenMutator.SelRect(
      {
        x: this.stagePosition.x,
        y: this.stagePosition.y,
        id: 'selRect'
      },
      {
        // Ren properties
        renStage: this
      },
      {
        // Ren events
      }
    )
    layer.add(renSelRect)
    layer.draw()
  }

  fitStageIntoParentContainer () {
    // Clears any unprocessed fitStage action (for performance reason)
    if (this._fitStageTimeout) {
      clearTimeout(this._fitStageTimeout)
    }
    // Schedules fitStage with a delay (for performace reason)
    this._fitStageTimeout = setTimeout(() => {
      console.log('[ren-stage][fitStageIntoParentContainer]')
      this.width(this.parentContainer.offsetWidth)
      this.height(window.innerHeight - 64)
      this.batchDraw()
    }, 100)
  }

  performZoom (zoomFactor = 1, zoomToPointer = false) {
    // https://konvajs.github.io/docs/sandbox/Zooming_Relative_To_Pointer.html
    const oldScale = this.scaleX()
    const newScale = Math.min(maxZoomLevel, Math.max(minZoomLevel, zoomFactor * oldScale))
    const pointer = this.getPointerPosition()
    const screenCenter = {
      x: this.width() / 2,
      y: this.height() / 2
    }
    const zoomPoint = zoomToPointer ? pointer : screenCenter
    const newStagePosition = {
      x: zoomPoint.x - (zoomPoint.x - this.x()) / oldScale * newScale,
      y: zoomPoint.y - (zoomPoint.y - this.y()) / oldScale * newScale
    }
    this.scale({ x: newScale, y: newScale })
    this.position(newStagePosition)

    if (this.zoomTimeout) {
      window.clearTimeout(this.zoomTimeout)
    }
    this.zoomTimeout = window.setTimeout(this._delayedZoom.bind(this), zoomDebounceDelay)
  }

  _delayedZoom () {
    this.batchDraw()
    this.adjustZoomLevel()
  }

  adjustZoomLevel () {
    const scale = this._scale = this.scaleX()
    const originalZoomLevel = this.zoomLevel
    while (scale > this.zoomLevel * 2) {
      this.zoomLevel *= 2
    }
    while (scale < this.zoomLevel / 2) {
      this.zoomLevel /= 2
    }
    if (this.zoomLevel !== originalZoomLevel) {
      this._renderGrid()
    }
    // ZOOM seems to work without redraw, even hiding levels seems to work as well
    // this._updateAllElements()
    // const elementLayers = this.getElementLayers()
    // elementLayers.forEach(layer => {
    //   layer.batchDraw()
    // })
    this.handleZoomLevelChanged(scale)
  }

  startAddingElementByType (elementType, assetId) {
    // Create temporary renElement
    const renElement = new RenElementAdding(
      {},
      {
        mutatorsLayer: this.findLayer('mutators'),
        renStage: this,
        _eventsParent: this
      },
      {
        handleUpdate: this.onElementUpdate
      },
      elementType,
      assetId
    )

    // Add to the layer
    const layer = this.findLayer('top')
    layer.add(renElement)
    layer.draw()
  }

  startSelectionMode (event) {
    // Initiating selRectMode
    if (this._visualMode === 'edit' && !this._addingElementMode) {
      const renSelRect = this.getRenElement('selRect', 'mutators')
      if (!renSelRect) {
        // Create new selRect (thus initiating the selRectMode)
        this._renderSelRect()
        this.selectionModeId = event.evt.timeStamp
      }
    }
  }

  endSelectionMode (event) {
    // Ending selRectMode
    const isNewEvent = !event || this.selectionModeId !== event.evt.timeStamp
    if (this._visualMode === 'edit' && !this._addingElementMode && isNewEvent) {
      const renSelRect = this.getRenElement('selRect', 'mutators')
      if (renSelRect) {
        // End selection (destroying the selRect)
        renSelRect.endSelection()
        this.selectionModeId = null
      }
    }
  }
  onKeyDown (evt) {
    if (evt.key === 'Escape' || evt.key === 'Esc') {
      // Cancel selection of an element
      this.handleElementsSelected([], false)
      // Cancel selection rectangle
      if (this.selRectMode) {
        const renSelRect = this.getRenElement('selRect', 'mutators')
        renSelRect.endSelection(true)
      }
    } else if (evt.key === 'Delete' || evt.key === 'Del') {
      // Delete all selected elements
      if (this._editMode) {
        this.handleDestroyManyElements(this._selectionIds)
      }
    } else if (evt.ctrlKey && (evt.key === 'a' || evt.key === 'A')) {
      // Ctrl + A: Select all elements
      const allElementsIds = this._elements.map(element => element.id)
      this.handleElementsSelected(allElementsIds, false)
    }
  }

  onElementClick (elementId, toggle) {
    // Handle
    this.handleElementsSelected([elementId], toggle) // elementIds must be an Array
  }

  onElementDragmove (elementId, delta) {
    // DEPRECATED
  }

  onElementUpdate (newElement) {
    // Add levelId (if missing)
    if (!newElement.levelId) {
      newElement.levelId = this._levelId
    }
    if (!newElement.visualId) {
      newElement.visualId = this.currentVisual.id
    }
    // Handle
    this.handleUpdateManyElements([newElement])
  }

  onSelectedElementsUpdate () {
    const newElements = this.selectedRenElements.map(renElement => {
      const element = Object.assign({}, renElement._element)
      element.position = Object.assign({}, element.position)
      element.position.points = renElement.getPoints(true)
      return element
    })

    // TODO: Snap

    this.handleUpdateManyElements(newElements)
  }

  setSizeAdjustment (sizeAdjustment) {
    this.sizeAdjustment = sizeAdjustment
    this._updateAllElements()
  }
}
